import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  NotebookPen, ShieldCheck, Check, Plus,
  Pencil, Trash2, Archive, ArchiveRestore, Clock, Undo2, Sparkles,
  HeartHandshake, MessageCircle, Fingerprint, ChevronLeft, ChevronRight,
} from "lucide-react";
import client from "../lib/api";
import { MoodPicker, MoodTag, DOWN_MOODS } from "../components/MoodPicker";
import { MOODS, MOOD_BY_KEY, moodTint } from "../components/moods";
import MoodFace from "../components/MoodFace";
import MoodTrend from "../components/MoodTrend";
import MoodInsights from "../components/MoodInsights";
import DailyCheckin from "../components/DailyCheckin";
import StreakFlame from "../components/StreakFlame";
import NoteNudge from "../components/NoteNudge";
import { gentleQuoteOfDay } from "../lib/quotes";

// A friendly relative label for a note's day.
function formatNoteDay(day) {
  if (!day) return "";
  try {
    const d = new Date(day + "T00:00:00");
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((today - d) / 86400000);
    if (diff <= 0) return "today";
    if (diff === 1) return "yesterday";
    if (diff < 7) return `${diff} days ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch (_e) { return day; }
}

function formatWhen(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (_e) { return iso; }
}

const AUDIT_LABEL = {
  created: "written", edited: "edited", archived: "archived",
  unarchived: "restored", deleted: "deleted", undeleted: "restored",
};

function iconBtn() {
  return "k-btn k-btn-ghost !p-1.5 !px-1.5";
}

/**
 * A single note. Owns its own edit / delete-confirm / history state so
 * the list stays simple. Delete bubbles up to the parent so it can offer
 * an undo window.
 */
// KLA-045: warm per-note tints, same language as the dashboard, so
// the journal reads like a wall of sticky colour instead of a form.
const JOURNAL_TINTS = [
  { bg: "hsl(var(--klareet-teal) / 0.07)", border: "hsl(var(--klareet-teal) / 0.22)", bar: "hsl(var(--klareet-teal))" },
  { bg: "hsl(24 92% 96%)", border: "hsl(24 80% 89%)", bar: "hsl(20 85% 55%)" },
  { bg: "hsl(255 50% 96%)", border: "hsl(255 38% 89%)", bar: "hsl(255 45% 60%)" },
  { bg: "hsl(203 60% 96%)", border: "hsl(203 48% 88%)", bar: "hsl(205 60% 48%)" },
  { bg: "hsl(330 70% 96%)", border: "hsl(330 55% 90%)", bar: "hsl(330 60% 58%)" },
];
function journalTint(id) {
  const h = [...String(id || "")].reduce((a, c) => a + c.charCodeAt(0), 0);
  return JOURNAL_TINTS[h % JOURNAL_TINTS.length];
}

function NoteCard({ note, archived, onChanged, onDelete, onTalk }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(note.text || "");
  const [editMood, setEditMood] = useState(note.mood || null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState(null);
  const [addedGoal, setAddedGoal] = useState(false);
  const [busy, setBusy] = useState(false);

  const saveEdit = async () => {
    const t = editText.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await client.patch(`/engagement/reflection/${note.id}`, { text: t, mood: editMood });
      setEditing(false);
      onChanged();
    } catch (_e) { /* silent */ } finally { setBusy(false); }
  };

  const doArchive = async () => {
    setBusy(true);
    try { await client.post(`/engagement/reflection/${note.id}/archive`); onChanged(); }
    catch (_e) { /* silent */ } finally { setBusy(false); }
  };
  const doUnarchive = async () => {
    setBusy(true);
    try { await client.post(`/engagement/reflection/${note.id}/unarchive`); onChanged(); }
    catch (_e) { /* silent */ } finally { setBusy(false); }
  };
  const doDelete = async () => {
    setBusy(true);
    setConfirmDelete(false);
    await onDelete(note.id);
    setBusy(false);
  };
  const addGoal = async () => {
    try {
      await client.post("/engagement/goal", { title: (note.text || "").slice(0, 90), detail: "from a note you wrote" });
      setAddedGoal(true);
    } catch (_e) { /* silent */ }
  };
  const toggleAudit = async () => {
    const next = !showAudit;
    setShowAudit(next);
    if (next && audit === null) {
      try {
        const { data } = await client.get(`/engagement/reflection/${note.id}/audit`);
        setAudit(data?.items || []);
      } catch (_e) { setAudit([]); }
    }
  };

  const tint = (note.mood && moodTint(note.mood)) || journalTint(note.id);
  return (
    <li
      className="k-card !p-4 relative overflow-hidden !pl-5"
      style={archived ? undefined : { background: tint.bg, border: `1px solid ${tint.border}` }}
      data-testid={`journal-note-${note.id}`}
    >
      {!archived && <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: tint.bar }} aria-hidden="true" />}
      {editing ? (
        <div>
          <textarea
            className="k-input klareet-serif"
            rows={3}
            maxLength={800}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            data-testid={`journal-note-${note.id}-edit-input`}
          />
          <div className="mt-2">
            <MoodPicker value={editMood} onChange={setEditMood} testid={`journal-note-${note.id}-edit-mood`} />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => { setEditing(false); setEditText(note.text || ""); setEditMood(note.mood || null); }} className="k-btn k-btn-ghost !py-1 !px-3 !text-xs" data-testid={`journal-note-${note.id}-edit-cancel`}>Cancel</button>
            <button onClick={saveEdit} disabled={!editText.trim() || busy} className="k-btn k-btn-primary !py-1 !px-3 !text-xs" data-testid={`journal-note-${note.id}-edit-save`}>Save</button>
          </div>
        </div>
      ) : (
        <div className="klareet-serif" style={{ fontSize: "1.05rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.55 }}>
          {note.text}
        </div>
      )}

      {!editing && (
        <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
          <div className="k-quiet !text-xs flex items-center gap-2">
            <span>{formatNoteDay(note.day)}</span>
            {note.mood && (<><span>·</span><MoodTag mood={note.mood} /></>)}
            {note.edit_count > 0 && (
              <span data-testid={`journal-note-${note.id}-edited`}>· edited</span>
            )}
            {note.fingerprint && (
              <span
                className="klareet-mono !text-[10px] inline-flex items-center gap-1"
                title={note.content_hash ? `signed · SHA-256 ${note.content_hash}` : "signed"}
                data-testid={`journal-note-${note.id}-fingerprint`}
              >
                · <Fingerprint size={11} /> {note.fingerprint}
              </span>
            )}
            {archived && <span className="k-chip !text-[10px] !py-0.5 !px-1.5" style={{ background: "hsl(var(--klareet-canvas-2))" }}>archived</span>}
          </div>

          {confirmDelete ? (
            <div className="flex items-center gap-2" data-testid={`journal-note-${note.id}-confirm`}>
              <span className="k-quiet !text-xs">Delete this note?</span>
              <button onClick={doDelete} disabled={busy} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" style={{ color: "hsl(var(--destructive))" }} data-testid={`journal-note-${note.id}-delete-confirm`}>Delete</button>
              <button onClick={() => setConfirmDelete(false)} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" data-testid={`journal-note-${note.id}-delete-cancel`}>Cancel</button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              {!archived && addedGoal ? (
                <span className="k-chip k-chip-teal !text-xs" data-testid={`journal-note-${note.id}-added`}><Check size={11} /> added</span>
              ) : !archived ? (
                <button onClick={addGoal} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" title="make a goal" data-testid={`journal-note-${note.id}-makegoal`}>
                  <Plus size={11} /> make a goal
                </button>
              ) : null}
              {!archived && (
                <button onClick={() => onTalk && onTalk(note.id)} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" title="talk to Klair about this" data-testid={`journal-note-${note.id}-talk`}>
                  <Sparkles size={11} /> talk to Klair
                </button>
              )}
              <button onClick={toggleAudit} className={iconBtn()} title="history" aria-label="history" data-testid={`journal-note-${note.id}-history`}><Clock size={14} /></button>
              {!archived && (
                <button onClick={() => setEditing(true)} className={iconBtn()} title="edit" aria-label="edit" data-testid={`journal-note-${note.id}-edit`}><Pencil size={14} /></button>
              )}
              {archived ? (
                <button onClick={doUnarchive} disabled={busy} className={iconBtn()} title="restore" aria-label="restore" data-testid={`journal-note-${note.id}-unarchive`}><ArchiveRestore size={14} /></button>
              ) : (
                <button onClick={doArchive} disabled={busy} className={iconBtn()} title="archive" aria-label="archive" data-testid={`journal-note-${note.id}-archive`}><Archive size={14} /></button>
              )}
              <button onClick={() => setConfirmDelete(true)} className={iconBtn()} title="delete" aria-label="delete" data-testid={`journal-note-${note.id}-delete`} style={{ color: "hsl(var(--klareet-muted))" }}><Trash2 size={14} /></button>
            </div>
          )}
        </div>
      )}

      {showAudit && (
        <div className="mt-3 pt-3" style={{ borderTop: "1px solid hsl(var(--klareet-line-soft))" }} data-testid={`journal-note-${note.id}-audit`}>
          <div className="k-label !mb-2 flex items-center gap-1.5"><Clock size={11} /> history</div>
          {audit === null ? (
            <div className="k-quiet !text-xs">loading.</div>
          ) : audit.length === 0 ? (
            <div className="k-quiet !text-xs">no changes recorded.</div>
          ) : (
            <ul className="space-y-1.5">
              {audit.map((a) => (
                <li key={a.id} className="text-xs flex items-start gap-2">
                  <span className="k-chip !text-[10px] !py-0.5 !px-1.5 shrink-0" style={{ background: "hsl(var(--klareet-canvas-2))" }}>{AUDIT_LABEL[a.action] || a.action}</span>
                  <span className="k-quiet">
                    {formatWhen(a.at)}
                    {a.action === "edited" && a.before ? <span className="block opacity-80" style={{ textDecoration: "line-through" }}>{a.before}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Journal. Every private note the person has written, newest first, plus
 * a composer at the top and full edit / archive / delete with an audit
 * trail and a short undo window on delete. A note can carry a mood and
 * become a goal, and Klair reads the latest active one when a check in
 * begins. Notes never affect any score.
 */
export default function JournalScreen({ product }) {
  const nav = useNavigate();
  const base = product === "grow" ? "/grow" : "";
  const [notes, setNotes] = useState([]);
  const [archivedNotes, setArchivedNotes] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [streak, setStreak] = useState({ streak_days: 0, last_days_logged: 0 });
  const [undo, setUndo] = useState(null);
  const [moodKey, setMoodKey] = useState(0);
  const [filterMood, setFilterMood] = useState(null);
  const [page, setPage] = useState(1);
  const undoTimer = useRef(null);

  const loadNotes = () => {
    client.get("/engagement/reflection")
      .then((r) => setNotes(r?.data?.items || []))
      .catch(() => setNotes([]));
  };
  const loadArchived = () => {
    client.get("/engagement/reflection?only_archived=true")
      .then((r) => setArchivedNotes(r?.data?.items || []))
      .catch(() => setArchivedNotes([]));
  };
  const loadStreak = () => {
    client.get("/engagement/streak").then((r) => setStreak(r.data || streak)).catch(() => {});
  };

  const refresh = () => { loadNotes(); loadArchived(); loadStreak(); setMoodKey((k) => k + 1); };

  useEffect(() => {
    refresh();
    return () => { if (undoTimer.current) clearTimeout(undoTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    try {
      await client.delete(`/engagement/reflection/${id}`);
      refresh();
      setUndo({ id });
      if (undoTimer.current) clearTimeout(undoTimer.current);
      undoTimer.current = setTimeout(() => setUndo(null), 6000);
    } catch (_e) { /* silent */ }
  };

  const undoDelete = async () => {
    if (!undo) return;
    const id = undo.id;
    setUndo(null);
    if (undoTimer.current) clearTimeout(undoTimer.current);
    try { await client.post(`/engagement/reflection/${id}/undelete`); refresh(); } catch (_e) { /* silent */ }
  };

  const focusComposer = () => {
    const el = document.querySelector('[data-testid="journal-note-input"]');
    if (el) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
  };

  const talkToKlair = (id) => nav(`${base}/checkin?note=${id}`);

  // Care rail: three logged moods in a row that read rough or worse.
  // Newest note first, so the most recent three moods decide it.
  const recentMoods = notes.filter((n) => n.mood).slice(0, 3).map((n) => n.mood);
  const careRail = recentMoods.length >= 3 && recentMoods.every((m) => DOWN_MOODS.includes(m));

  const PAGE_SIZE = 8;
  const presentMoods = MOODS.filter((m) => notes.some((n) => n.mood === m.key));
  const filtered = filterMood ? notes.filter((n) => n.mood === filterMood) : notes;
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageNotes = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className={`relative max-w-3xl mx-auto px-5 pt-8 pb-24 ${careRail ? "k-carerail" : ""}`} data-testid="journal-screen">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div className="min-w-[280px] flex-1">
          <span className="mf-eyebrow" data-testid="journal-eyebrow"><NotebookPen size={14} /> Daily check-in</span>
          <h1 className="klareet-serif mt-3 mb-2" style={{ fontSize: "clamp(1.7rem, 4vw, 2.3rem)", lineHeight: 1.12 }} data-testid="journal-title">
            A quiet place to <span className="mf-mark">think out loud.</span>
          </h1>
          <p className="k-hero-sub" style={{ maxWidth: "56ch" }}>
            Every note stays private to you. Klair reads your most recent one when you check in, so it can meet you where you are. Nothing here is a score, and every change is logged and signed so nothing gets lost.
          </p>
        </div>
        {streak.streak_days > 0 && <StreakFlame days={streak.streak_days} />}
      </div>

      {/* Privacy disclaimer. Plain and up front. */}
      <div
        className="rounded-xl p-3 mb-6 flex items-start gap-2.5 text-sm"
        style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))", color: "hsl(var(--klareet-ink-soft))" }}
        data-testid="journal-privacy-disclaimer"
      >
        <ShieldCheck size={15} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--klareet-teal))" }} />
        <span style={{ lineHeight: 1.5 }}>
          Your notes are private to you. Klareet never shows an individual note to your college. Only anonymous, aggregate patterns, never your words, are shared with your institution.
        </span>
      </div>

      {careRail && (
        <div
          className="rounded-2xl p-5 mb-6 k-fade-in"
          style={{ border: "1px solid hsl(var(--klareet-red) / 0.35)", background: "hsl(var(--klareet-red) / 0.06)" }}
          data-testid="journal-care-rail"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "hsl(var(--klareet-red) / 0.14)", color: "hsl(var(--klareet-red))" }}>
              <HeartHandshake size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="k-label mb-1" style={{ color: "hsl(var(--klareet-red))" }}>a gentle check</div>
              <div className="klareet-serif" style={{ fontSize: "1.15rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.4 }} data-testid="care-rail-message">
                I noticed the last few days have felt rough. I am here. Want to talk about what is going on?
              </div>
              <p className="klareet-serif mt-3" style={{ fontStyle: "italic", color: "hsl(var(--klareet-ink-soft))", lineHeight: 1.5 }} data-testid="care-rail-quote">
                {gentleQuoteOfDay()}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => nav(`${base}/ask`)} className="k-btn k-btn-primary !text-sm" data-testid="care-rail-talk">
                  <MessageCircle size={14} /> Talk to Klair
                </button>
                <button onClick={() => nav(`${base}/checkin`)} className="k-btn k-btn-outline !text-sm" data-testid="care-rail-checkin">
                  Do a check in
                </button>
              </div>
              <p className="k-quiet !text-xs mt-3">
                If things feel heavy, talking to someone you trust helps too. You are not alone in this.
              </p>
            </div>
          </div>
        </div>
      )}

      <NoteNudge lastNoteDay={notes[0]?.day || null} onWrite={focusComposer} />

      {/* Composer: the redesigned daily check-in card */}
      <div className="mb-8">
        <DailyCheckin
          onSaved={refresh}
          textareaTestid="journal-note-input"
          saveTestid="journal-save"
          moodTestid="journal-mood"
          rootTestid="journal-composer"
        />
      </div>

      {/* Mood insights: overall, this month, last month, month by month */}
      <div className="mb-6">
        <MoodInsights refreshKey={moodKey} />
      </div>

      {/* Mood trend, week by week */}
      <div className="mb-8">
        <MoodTrend refreshKey={moodKey} />
      </div>

      {/* History with a mood filter and pagination */}
      {notes.length === 0 ? (
        <div className="k-quiet text-center py-10" data-testid="journal-empty">
          No notes yet. Your first one starts the streak.
        </div>
      ) : (
        <>
          {presentMoods.length > 0 && (
            <div className="mf-filter mb-4" data-testid="journal-filter">
              <button
                type="button"
                className={`mf-fchip mf-fchip-all ${!filterMood ? "is-on" : ""}`}
                style={!filterMood ? { "--deep": "#6D4AE0", "--wash": "#EDE7FB" } : undefined}
                onClick={() => { setFilterMood(null); setPage(1); }}
                data-testid="journal-filter-all"
              >
                all
              </button>
              {presentMoods.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={`mf-fchip ${filterMood === m.key ? "is-on" : ""}`}
                  style={{ "--deep": m.c2, "--wash": `${m.c2}14` }}
                  onClick={() => { setFilterMood(filterMood === m.key ? null : m.key); setPage(1); }}
                  aria-pressed={filterMood === m.key}
                  data-testid={`journal-filter-${m.key}`}
                >
                  <MoodFace moodKey={m.key} size={18} animate={false} /> {m.label}
                </button>
              ))}
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="k-quiet text-center py-8" data-testid="journal-filter-empty">
              No {filterMood ? (MOOD_BY_KEY[filterMood] && MOOD_BY_KEY[filterMood].label) : ""} notes yet.
            </div>
          ) : (
            <ul className="space-y-3" data-testid="journal-list">
              {pageNotes.map((n) => (
                <NoteCard key={n.id} note={n} archived={false} onChanged={refresh} onDelete={handleDelete} onTalk={talkToKlair} />
              ))}
            </ul>
          )}

          {pageCount > 1 && (
            <div className="mf-pager" data-testid="journal-pager">
              <button
                type="button"
                className="mf-pager-btn"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="journal-pager-prev"
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span className="mf-pager-info" data-testid="journal-pager-info">page {safePage} of {pageCount}</span>
              <button
                type="button"
                className="mf-pager-btn"
                disabled={safePage >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                data-testid="journal-pager-next"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Archived */}
      {archivedNotes.length > 0 && (
        <div className="mt-8">
          <button
            onClick={() => setShowArchived((s) => !s)}
            className="k-btn k-btn-ghost !py-1.5 !px-3 !text-xs"
            data-testid="journal-show-archived"
          >
            <Archive size={13} /> {showArchived ? "Hide" : "Show"} archived ({archivedNotes.length})
          </button>
          {showArchived && (
            <ul className="space-y-3 mt-3" data-testid="journal-archived-list">
              {archivedNotes.map((n) => (
                <NoteCard key={n.id} note={n} archived onChanged={refresh} onDelete={handleDelete} />
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Undo delete window */}
      {undo && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-6 z-50 rounded-full py-2.5 px-5 flex items-center gap-4 shadow-lg k-fade-in"
          style={{ background: "hsl(var(--klareet-ink))", color: "white" }}
          data-testid="undo-banner"
        >
          <span className="text-sm flex items-center gap-2"><Trash2 size={13} /> Note deleted</span>
          <button onClick={undoDelete} className="text-sm font-semibold flex items-center gap-1.5 underline underline-offset-2" data-testid="undo-delete">
            <Undo2 size={13} /> Undo
          </button>
        </div>
      )}
    </div>
  );
}
